import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'
import SAlert from 'components/SAlert'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import publishBudgetIcon from '../../assets/images/billing-icon-w.svg'
import collapseIcon from '../../assets/images/collapse-alt-svgrepo-com-w.svg'
import expandIcon from '../../assets/images/expand-svgrepo-com-w.svg'
import filterSolidIcon from '../../assets/images/filter-solid-icon.svg'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import { BudgetValidate } from '../../resources/form-validator'
import {
  fileDownloadBlob,
  nextInclomeTaxYears,
  nextYears,
} from '../../resources/functions'
import { useBudgetApi } from '../../resources/hooks/api/useBudgetApiHook'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import { getBudgetSettings } from '../../store/settings/selectors'
import { getBranch } from '../../store/user/selectors'
import './budget.css'
import BudgetRow from './BudgetRow'
import CustomTreeSelect from './Customtreeselect'
import {
  ApiNode,
  collectFormIndexes,
  convertTree,
  findSubtreeById,
  flattenForForm,
  sumDescendants,
  TreeNode,
} from './HelperTree'
import BudgetTableHeader from './Budgettableheader'

const COL_CHECKBOX = 40
const COL_NAME = '30%'
const COL_MONTH = 150
const COL_TOTAL = 140

// ─── Flat virtual item types (REMOVED gh-row) ─────────────────────────────────
type FlatItem =
  | { type: 'gh-toggle'; ghNode: TreeNode }
  | { type: 'sh-header'; node: TreeNode }
  | { type: 'ssh-header'; node: TreeNode }
  | { type: 'leaf'; node: TreeNode }

const ITEM_HEIGHT: Record<FlatItem['type'], number> = {
  'gh-toggle': 52,
  'sh-header': 44,
  'ssh-header': 44,
  leaf: 56,
}

interface BudgetField {
  master_chart_of_account_number: string
  master_chart_of_account_name: string
  master_chart_of_account_id: number
  parent_id: number
  report_code: string
  [key: string]: any
}
interface AddBudgetIFormI {
  budgets: BudgetField[]
}

const Budget: React.FC = () => {
  // ── 1. Hooks & Redux Selectors ──────────────────────────────────────────
  const { t } = useTranslation('scenes.Budget.index')
  const budgetSettings = useSelector(getBudgetSettings)
  const {
    fetchBudget,
    postBudget,
    updateBudget,
    publishBudget,
    downloadBudgetReport,
    downloadBudgetReportExcel,
  } = useBudgetApi()
  const { fetchAccountSettingsData } = useSettingsApi()
  const branchData = useSelector(getBranch)

  // ── 2. Form Setup (React Hook Form) ────────────────────────────────────
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<AddBudgetIFormI>({
    defaultValues: { budgets: [] },
    resolver: yupResolver(BudgetValidate),
  })
  const { fields, append: formAppend } = useFieldArray({
    control,
    name: 'budgets',
  })
  const allBudgets = useWatch({ control, name: 'budgets' })

  // ── 3. State & Refs ────────────────────────────────────────────────────
  const [budgetTree, setBudgetTree] = useState<TreeNode[]>([])
  const originalRootsRef = useRef<TreeNode[]>([])
  const [openGhIds, setOpenGhIds] = useState<Set<number>>(new Set())
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<number>>(
    new Set()
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [accountTypeOptions, setAccountTypeOptions] = useState<any[]>([])
  const [selectedAccountType, setSelectedAccountType] = useState<any>(null)
  const [searchParams, setSearchParams] = useState<any>({ year: null })
  const [isLoading, setIsLoading] = useState(false)
  const [isRecordsExists, setIsRecordsExists] = useState(false)
  const [isBudgetPublished, setIsBudgetPublished] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [tableHeaders, setTableHeaders] = useState<any[]>([])
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [showBudgetAlert, setShowBudgetAlert] = useState({
    timestamp: new Date().getTime(),
    show: false,
  })
  const [uploadErrors, setUploadErrors] = useState<
    { row: number; column?: string; message: string }[]
  >([])
  const [showErrorModal, setShowErrorModal] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const editedValuesRef = useRef<Record<number, Record<string, string>>>({})

  // Derived state flags
  const isPastYear =
    (searchParams?.year?.value ?? new Date().getFullYear()) <
    new Date().getFullYear()
  const isReadOnly = isBudgetPublished || isPastYear

  // ── 4. Effects ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAccountSettingsData(
      { branch_id: +branchData['id'] },
      () => {},
      () => {}
    )
  }, [])

  // ── 5. Utility Helpers & Formatters ────────────────────────────────────
  const normalizeBudgetAmountValue = (value: any): number => {
    if (!value) return 0
    const n = parseFloat(String(value).replace(/,/g, ''))
    return isFinite(n) ? n : 0
  }

  const formatBudgetAmountInputValue = (value: any): string => {
    const n = normalizeBudgetAmountValue(value)
    return n === 0 ? '0' : n.toFixed(2)
  }

  const monthTransform = useCallback(
    (month: any) => {
      const map: any = {
        jan: t('short_month.01', { ns: 'resources.form-validator' }),
        feb: t('short_month.02', { ns: 'resources.form-validator' }),
        mar: t('short_month.03', { ns: 'resources.form-validator' }),
        apr: t('short_month.04', { ns: 'resources.form-validator' }),
        may: t('short_month.05', { ns: 'resources.form-validator' }),
        jun: t('short_month.06', { ns: 'resources.form-validator' }),
        jul: t('short_month.07', { ns: 'resources.form-validator' }),
        aug: t('short_month.08', { ns: 'resources.form-validator' }),
        sep: t('short_month.09', { ns: 'resources.form-validator' }),
        oct: t('short_month.10', { ns: 'resources.form-validator' }),
        nov: t('short_month.11', { ns: 'resources.form-validator' }),
        dec: t('short_month.12', { ns: 'resources.form-validator' }),
      }
      return map[month] || month
    },
    [t]
  )

  const getLeafTotal = useCallback(
    (formIndex: number): number => {
      const row = allBudgets?.[formIndex]
      if (!row) return 0
      return Object.keys(row).reduce((acc, key) => {
        if (!key.startsWith('month_')) return acc
        const n = parseFloat(String(row[key]?.amount ?? '0').replace(/,/g, ''))
        return acc + (isFinite(n) ? n : 0)
      }, 0)
    },
    [allBudgets]
  )

  const getNodeTotal = useCallback(
    (node: TreeNode) => sumDescendants(node, getLeafTotal),
    [getLeafTotal]
  )

  // ── 6. Event Handlers & API Actions ─────────────────────────────────────
  const toggleGh = useCallback((id: number) => {
    setOpenGhIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const toggleNode = useCallback((id: number) => {
    setCollapsedNodeIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const buildFormFromRoots = (roots: TreeNode[]) => {
    const editableNodes = flattenForForm(roots)
    if (editableNodes.length) setTableHeaders(editableNodes[0].details)
    reset({ budgets: [] })
    editableNodes.forEach((node) => {
      const savedEdits = editedValuesRef.current[node.id]
      const monthAmounts: any = {}
      node.details.forEach((detail, monthIndex) => {
        const monthKey = `month_${monthIndex}`
        const rawValue = savedEdits?.[monthKey] ?? detail.amount
        monthAmounts[monthKey] = {
          amount: formatBudgetAmountInputValue(rawValue),
        }
      })
      formAppend({
        master_chart_of_account_number: node.account,
        master_chart_of_account_name: node.accountName,
        master_chart_of_account_id: node.id,
        parent_id: 0,
        report_code: node.reportCode,
        ...monthAmounts,
      })
    })
    setSelectedRows([])
    setOpenGhIds(new Set(roots.map((r) => r.id)))
    setCollapsedNodeIds(new Set())
  }

  const handleSelectRow = (index: number) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  const handleGroupSelectAll = (formIndexes: number[]) => {
    const allSelected = formIndexes.every((i) => selectedRows.includes(i))
    setSelectedRows((prev) =>
      allSelected
        ? prev.filter((i) => !formIndexes.includes(i))
        : Array.from(new Set([...prev, ...formIndexes]))
    )
  }

  const onValueChange = (rowIndex: number, colIndex: number, val: string) => {
    const accountId = getValues(
      `budgets.${rowIndex}.master_chart_of_account_id`
    )
    if (!editedValuesRef.current[accountId])
      editedValuesRef.current[accountId] = {}
    editedValuesRef.current[accountId][`month_${colIndex}`] = val
  }

  const handleFillAcross = () => {
    if (!selectedRows.length) return toast.error(t('toast.selectAccountToFill'))
    let hasValue = false
    selectedRows.forEach((rowIndex) => {
      const first = getValues(`budgets.${rowIndex}.month_0.amount`)
      const n = normalizeBudgetAmountValue(first)
      if (n <= 0) return
      hasValue = true
      const formatted = formatBudgetAmountInputValue(first)
      const accountId = getValues(
        `budgets.${rowIndex}.master_chart_of_account_id`
      )
      if (!editedValuesRef.current[accountId])
        editedValuesRef.current[accountId] = {}
      tableHeaders.forEach((_, colIndex) => {
        setValue(`budgets.${rowIndex}.month_${colIndex}.amount`, formatted)
        editedValuesRef.current[accountId][`month_${colIndex}`] = formatted
      })
    })
    if (!hasValue) return toast.error(t('toast.enterValueFirstCell'))
    toast.success(t('toast.fillSuccess'))
  }

  const handleDistributeEvenly = () => {
    if (!selectedRows.length)
      return toast.error(t('toast.selectAccountToDistribute'))
    let hasProcessed = false
    selectedRows.forEach((rowIndex) => {
      const first = getValues(`budgets.${rowIndex}.month_0.amount`)
      const total = normalizeBudgetAmountValue(first)
      if (total <= 0) return
      hasProcessed = true
      const split = (total / tableHeaders.length).toFixed(2)
      const accountId = getValues(
        `budgets.${rowIndex}.master_chart_of_account_id`
      )
      if (!editedValuesRef.current[accountId])
        editedValuesRef.current[accountId] = {}
      tableHeaders.forEach((_, colIndex) => {
        setValue(`budgets.${rowIndex}.month_${colIndex}.amount`, split, {
          shouldValidate: true,
          shouldDirty: true,
        })
        editedValuesRef.current[accountId][`month_${colIndex}`] = split
      })
    })
    if (!hasProcessed) return toast.error(t('toast.enterValueFirstCell'))
    toast.success(t('toast.distributeSuccess'))
  }

  const onChangeAccountType = (selected: {
    id: number | string | null
    label: string
  }) => {
    // 1. Reset filter if "All" or null is selected
    if (
      selected.id === null ||
      selected.id === '' ||
      selected.id === undefined
    ) {
      setBudgetTree(originalRootsRef.current)
      buildFormFromRoots(originalRootsRef.current)
      return
    }

    // 2. Search recursively across top-level roots and nested children
    const filtered = findSubtreeById(originalRootsRef.current, selected.id)

    if (filtered.length === 0) {
      // Fallback if no matching node was found
      setBudgetTree([])
      reset({ budgets: [] })
      return
    }

    // 3. Update tree and rebuild form rows with the filtered subtree
    setBudgetTree(filtered)
    buildFormFromRoots(filtered)
  }

  const fetchBudgetDetails = (year: any) => {
    if (!year) return setSearchError(t('msg.selectYear'))
    setIsLoading(true)
    setBudgetTree([])
    setOpenGhIds(new Set())
    setCollapsedNodeIds(new Set())
    setAccountTypeOptions([])
    setSelectedAccountType(null)
    reset({ budgets: [] })
    editedValuesRef.current = {}
    originalRootsRef.current = []

    fetchBudget(
      { year: +year, branch_id: +branchData['id'] },
      (msg: string, resp: any) => {
        setIsLoading(false)
        setIsRecordsExists(resp.data.data.budget_exists)
        setIsBudgetPublished(resp.data.data.budget_published)
        const rawData: ApiNode[] = resp.data.data.data
        // const rawData: ApiNode[] = dummyResponse.data
        const roots = convertTree(rawData)
        originalRootsRef.current = roots
        setBudgetTree(roots)
        buildFormFromRoots(roots)
        const opts: any[] = [{ id: '', label: t('label.all', 'All') }]
        roots.forEach((r) =>
          opts.push({ id: r.id, label: `${r.account} ${r.accountName}` })
        )
        setAccountTypeOptions(opts)
        setSelectedAccountType(opts[0])
      },
      (msg: string) => {
        setIsLoading(false)
        toast.error(msg)
      }
    )
  }

  const saveBudget = (data: AddBudgetIFormI) => {
    const selectedYear = searchParams?.year?.value
    const branchId = branchData?.id

    if (!selectedYear) {
      toast.error(t('msg.selectYear'))
      return
    }

    // 1. Sync form edits to ref cache
    data.budgets.forEach((record) => {
      const accountId = record.master_chart_of_account_id
      if (!editedValuesRef.current[accountId]) {
        editedValuesRef.current[accountId] = {}
      }
      tableHeaders.forEach((_, colIndex) => {
        editedValuesRef.current[accountId][`month_${colIndex}`] = String(
          record[`month_${colIndex}`]?.amount ?? '0'
        )
      })
    })

    // 2. Recursively extract all editable leaf nodes
    const collectLeaves = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.flatMap((node) =>
        node.isEditable ? [node] : collectLeaves(node.children || [])
      )
    }
    const allLeaves = collectLeaves(originalRootsRef.current)

    // 3. Format payload records
    const formData = allLeaves.flatMap((node) => {
      const savedEdits = editedValuesRef.current[node.id]
      return tableHeaders.map((cal: any, i: number) => ({
        master_chart_of_account_id: node.id,
        parent_id: node.id,
        total_amount: String(
          savedEdits?.[`month_${i}`] ?? node.details[i]?.amount ?? 0
        ).replace(/,/g, ''),
        month: cal.month_count,
        year: cal.year,
      }))
    })

    const params = {
      year: +selectedYear,
      branch_id: +branchId,
      formData,
    }

    // 4. API Response Handlers
    const onSuccess = (message: string) => {
      setIsLoading(false)
      fetchBudgetDetails(selectedYear)
      toast.success(message)
    }

    const onError = (message: string) => {
      setIsLoading(false)
      toast.error(message)
    }

    // 5. Dispatch
    setIsLoading(true)
    if (isRecordsExists) {
      updateBudget(params, onSuccess, onError)
    } else {
      postBudget(params, onSuccess, onError)
    }
  }

  const publishBudgetConfirmation = () =>
    setShowBudgetAlert({ timestamp: new Date().getTime(), show: true })

  const closeBudgetModal = () =>
    setShowBudgetAlert({ timestamp: new Date().getTime(), show: false })

  const handlePublishBudget = () => {
    closeBudgetModal()
    setIsLoading(true)
    publishBudget(
      { year: +searchParams['year']['value'], branch_id: +branchData['id'] },
      (message: string) => {
        setIsLoading(false)
        fetchBudgetDetails(searchParams['year']['value'])
        toast.success(message)
      },
      (message: string) => {
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const _handleDownloadReport = () => {
    if (!searchParams?.year?.value) return setSearchError(t('msg.selectYear'))
    setIsLoading(true)
    downloadBudgetReport(
      { year: +searchParams['year']['value'], branch_id: +branchData['id'] },
      (message: string, resp: any) => {
        setIsLoading(false)
        fileDownloadBlob(resp, `budget_report_${searchParams['year']['value']}`)
      },
      (message: string) => {
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const _handleDownloadExcel = () => {
    if (!searchParams?.year?.value) return setSearchError(t('msg.selectYear'))
    setIsLoading(true)
    downloadBudgetReportExcel(
      {
        year: +searchParams['year']['value'],
        branch_id: +branchData['id'],
        format: 'excel',
      },
      (message: string, resp: any) => {
        setIsLoading(false)
        const url = window.URL.createObjectURL(
          new Blob([resp], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
        )
        const a = document.createElement('a')
        a.href = url
        a.download = `budget_report_${searchParams['year']['value']}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      },
      (message: string) => {
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const downloadTemplate = async () => {
    // 1. Collect all nodes with reportCode === 'VP'
    const collectVpNodes = (nodes: TreeNode[]): TreeNode[] => {
      let vpNodes: TreeNode[] = []
      nodes.forEach((node) => {
        if (node.reportCode === 'VP') {
          vpNodes.push(node)
        }
        if (node.children && node.children.length > 0) {
          vpNodes = vpNodes.concat(collectVpNodes(node.children))
        }
      })
      return vpNodes
    }

    const vpAccounts = collectVpNodes(originalRootsRef.current)

    if (!vpAccounts.length) {
      toast.error(t('toast.noVpAccounts', 'No VP accounts found to export'))
      return
    }

    // 2. Build Column Headers (Account Code, Account Name, Months)
    const headers = [
      t('label.accountCode', 'Account Code'),
      t('label.accountName', 'Account Name'),
      ...tableHeaders.map(
        (cal) => `${monthTransform(cal?.month)} ${cal?.year}`
      ),
    ]

    try {
      // 3. Create ExcelJS workbook and worksheet with frozen header row
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Budget Template', {
        views: [{ state: 'frozen', xSplit: 2, ySplit: 1 }],
      })

      // Add header row with styling
      const headerRow = worksheet.addRow(headers)
      headerRow.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 }
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '00205C' }, // Navy blue theme
      }
      headerRow.height = 26

      // Add VP Account data rows
      vpAccounts.forEach((account) => {
        const rowData = [
          account.account,
          account.accountName,
          ...tableHeaders.map((_, i) => account.details[i]?.amount ?? 0),
        ]
        const row = worksheet.addRow(rowData)
        row.height = 22

        // Format month amount columns (Right-aligned, 2 decimal places)
        for (let col = 3; col <= headers.length; col++) {
          const cell = row.getCell(col)
          cell.numFmt = '#,##0.00'
          cell.alignment = { horizontal: 'right', vertical: 'middle' }
        }
      })

      // Set column widths dynamically
      worksheet.columns.forEach((col:any, idx:any) => {
        const headerLen = headers[idx] ? String(headers[idx]).length : 12
        col.width = Math.max(headerLen + 4, idx === 1 ? 30 : 15)
      })

      // 4. Export file using file-saver
      const buffer = await workbook.xlsx.writeBuffer()
      const fileName = `budget_template_${
        searchParams?.year?.value || new Date().getFullYear()
      }.xlsx`
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName)
      toast.success(
        t('toast.templateDownloaded', 'Template downloaded successfully')
      )
    } catch (error) {
      console.error('Error generating Excel template:', error)
      toast.error(t('toast.downloadError', 'Failed to generate Excel template'))
    }
  }

  const uploadBudget = () => {
    if (isReadOnly) {
      toast.error(
        t('toast.readOnlyMode', 'Cannot upload budget in read-only mode')
      )
      return
    }
    fileInputRef.current?.click()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsLoading(true)

    // Defer heavy Excel processing to next tick so React paints the loading spinner first
    setTimeout(async () => {
      try {
        const arrayBuffer = await file.arrayBuffer()
        const workbook = new ExcelJS.Workbook()
        await workbook.xlsx.load(arrayBuffer)

        const worksheet = workbook.worksheets[0]
        if (!worksheet || worksheet.rowCount < 2) {
          setUploadErrors([
            { row: 0, message: t('msg.emptyExcel', 'Excel file is empty.') },
          ])
          setShowErrorModal(true)
          setIsLoading(false)
          event.target.value = ''
          return
        }

        // Read headers from row 1
        const rawHeaders: string[] = []
        const headerRow = worksheet.getRow(1)
        headerRow.eachCell({ includeEmpty: true }, (cell:any, colNumber:any) => {
          rawHeaders[colNumber - 1] = String(cell.value ?? '')
            .trim()
            .toUpperCase()
        })

        // Read row objects starting from row 2
        const rows: Record<string, any>[] = []
        worksheet.eachRow((row:any, rowNumber:any) => {
          if (rowNumber === 1) return // Skip header row
          const rowData: Record<string, any> = {}
          row.eachCell({ includeEmpty: true }, (cell:any, colNumber:any) => {
            const headerKey = rawHeaders[colNumber - 1]
            if (headerKey) {
              const rawVal =
                typeof cell.value === 'object' &&
                cell.value !== null &&
                'result' in cell.value
                  ? (cell.value as any).result
                  : cell.value
              rowData[headerKey] = rawVal ?? ''
            }
          })
          if (Object.keys(rowData).length > 0) {
            rows.push(rowData)
          }
        })

        validateAndPatchExcel(rawHeaders, rows)
      } catch (err) {
        console.error('Error reading Excel file:', err)
        toast.error(t('toast.readError', 'Failed to read the Excel file.'))
      } finally {
        setIsLoading(false)
        event.target.value = ''
      }
    }, 50)
  }

  const validateAndPatchExcel = (
    actualHeaders: string[],
    rows: Record<string, any>[]
  ) => {
    const errors: { row: number; column?: string; message: string }[] = []

    if (!rows.length) {
      errors.push({
        row: 0,
        message: t('msg.emptyExcel', 'Excel file is empty.'),
      })
      setUploadErrors(errors)
      setShowErrorModal(true)
      return
    }

    const norm = (s: string) =>
      String(s || '')
        .trim()
        .toUpperCase()
        .replace(/[\s\-_]/g, '')
    const normalizedActualHeaders = actualHeaders.map(norm)

    // Locate Account Code column header key
    const accountColKey = actualHeaders.find((h) =>
      ['ACCOUNT CODE', 'ACCOUNT NO', 'ACCOUNT_NO', 'ACCOUNT'].includes(
        h.trim().toUpperCase()
      )
    )

    if (!accountColKey) {
      errors.push({
        row: 0,
        column: t('label.accountCode', 'Account Code'),
        message: t(
          'error.missingAccountCol',
          'Missing Account Code column (e.g. ACCOUNT CODE or ACCOUNT NO).'
        ),
      })
    }

    // Map current tableHeaders (months) to expected normalized labels
    const monthHeaderMap = tableHeaders.map((cal, monthIndex) => {
      const displayMonth = monthTransform(cal?.month)
      const rawMonth = String(cal?.month || '')
      return {
        monthIndex,
        expectedLabel: `${displayMonth} ${cal?.year}`,
        normLabel1: norm(`${displayMonth}${cal?.year}`),
        normLabel2: norm(`${rawMonth}${cal?.year}`),
      }
    })

    // Check header presence
    monthHeaderMap.forEach((m) => {
      const found = normalizedActualHeaders.some(
        (h) => h === m.normLabel1 || h === m.normLabel2
      )
      if (!found) {
        errors.push({
          row: 0,
          column: m.expectedLabel,
          message: t('error.missingCol', 'Missing column: {{col}}', {
            col: m.expectedLabel,
          }),
        })
      }
    })

    if (errors.length > 0) {
      setUploadErrors(errors)
      setShowErrorModal(true)
      return
    }

    // Create lookup map of current editable form rows by account code
    const budgetFields = getValues('budgets') || []
    const budgetMap = new Map<
      string,
      { field: BudgetField; formIndex: number }
    >()

    budgetFields.forEach((field, index) => {
      const accNum = String(field.master_chart_of_account_number).trim()
      budgetMap.set(accNum, { field, formIndex: index })
    })

    const duplicateAccounts = new Set<string>()
    const validRowsData: {
      formIndex: number
      accountId: number
      monthValues: { monthIndex: number; value: string }[]
    }[] = []

    rows.forEach((row, index) => {
      const rowNumber = index + 2 // 1-based index (header is row 1)
      const rawAccountVal = row[accountColKey!]
      const accountCodeStr = String(rawAccountVal ?? '').trim()

      if (!accountCodeStr) {
        errors.push({
          row: rowNumber,
          column: t('label.accountCode', 'Account Code'),
          message: t('msg.accountRequired', 'Account Code is required.'),
        })
        return
      }

      if (duplicateAccounts.has(accountCodeStr)) {
        errors.push({
          row: rowNumber,
          column: t('label.accountCode', 'Account Code'),
          message: t(
            'error.duplicateAccountCode',
            'Duplicate Account Code {{code}}.',
            { code: accountCodeStr }
          ),
        })
        return
      }
      duplicateAccounts.add(accountCodeStr)

      const matchedBudget = budgetMap.get(accountCodeStr)
      if (!matchedBudget) {
        errors.push({
          row: rowNumber,
          column: t('label.accountCode', 'Account Code'),
          message: t(
            'error.accountCodeNotFound',
            'Account Code {{code}} not found in budget.',
            { code: accountCodeStr }
          ),
        })
        return
      }

      const monthValues: { monthIndex: number; value: string }[] = []

      // Validate month values for row
      monthHeaderMap.forEach((m) => {
        const rowKey = Object.keys(row).find(
          (k) => norm(k) === m.normLabel1 || norm(k) === m.normLabel2
        )
        const val = rowKey !== undefined ? row[rowKey] : ''

        if (val === '' || val === undefined || val === null) {
          monthValues.push({ monthIndex: m.monthIndex, value: '0.00' })
          return
        }

        const strVal = String(val).replace(/,/g, '').trim()
        if (isNaN(Number(strVal))) {
          errors.push({
            row: rowNumber,
            column: m.expectedLabel,
            message: t('error.mustBeNumeric', '{{col}} must be numeric.', {
              col: m.expectedLabel,
            }),
          })
          return
        }

        const numVal = Number(strVal)
        if (numVal < 0) {
          errors.push({
            row: rowNumber,
            column: m.expectedLabel,
            message: t(
              'error.cannotBeNegative',
              '{{col}} cannot be negative.',
              { col: m.expectedLabel }
            ),
          })
          return
        }

        const decimalPart = strVal.includes('.') ? strVal.split('.')[1] : ''
        if (decimalPart.length > 2) {
          errors.push({
            row: rowNumber,
            column: m.expectedLabel,
            message: t(
              'error.mustHaveDecimals',
              '{{col}} must have at most 2 decimal places.',
              { col: m.expectedLabel }
            ),
          })
          return
        }

        monthValues.push({ monthIndex: m.monthIndex, value: numVal.toFixed(2) })
      })

      validRowsData.push({
        formIndex: matchedBudget.formIndex,
        accountId: matchedBudget.field.master_chart_of_account_id,
        monthValues,
      })
    })

    if (errors.length > 0) {
      setUploadErrors(errors)
      setShowErrorModal(true)
      return
    }

    // ── PERFORMANCE OPTIMIZATION: Single Batch Atomic Update ──
    // Instead of calling setValue 1,200 times in a loop (which triggers 1,200 full re-renders + Yup validations),
    // we construct the updated budgets array in memory and apply a single batch update!
    const currentBudgets = getValues('budgets') || []
    const updatedBudgets = [...currentBudgets]

    validRowsData.forEach(({ formIndex, accountId, monthValues }) => {
      if (!editedValuesRef.current[accountId]) {
        editedValuesRef.current[accountId] = {}
      }
      const updatedRow = { ...updatedBudgets[formIndex] }

      monthValues.forEach(({ monthIndex, value }) => {
        const monthKey = `month_${monthIndex}`
        updatedRow[monthKey] = { amount: value }
        editedValuesRef.current[accountId][monthKey] = value
      })

      updatedBudgets[formIndex] = updatedRow
    })

    // Single atomic update to React Hook Form (1 re-render instead of N*12)
    setValue('budgets', updatedBudgets, {
      shouldDirty: true,
      shouldValidate: true,
    })

    toast.success(t('toast.uploadSuccess', 'Budget imported successfully.'))
  }

  // ── 7. Virtualizer & Memoized Render Lists ─────────────────────────────
  const flatItems = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = []
    const cleanSearch = searchTerm.trim().toLowerCase()

    // Helper function to check if a node or any of its descendants match the search criteria
    const matchesSearch = (node: TreeNode): boolean => {
      if (!cleanSearch) return true

      const currentMatches =
        node.account.toLowerCase().includes(cleanSearch) ||
        node.accountName.toLowerCase().includes(cleanSearch)

      if (currentMatches) return true
      if (node.children && node.children.length > 0) {
        return node.children.some((child) => matchesSearch(child))
      }
      return false
    }

    budgetTree.forEach((ghNode) => {
      // Skip rendering this entire group if nothing inside matches the search query
      if (!matchesSearch(ghNode)) return

      items.push({ type: 'gh-toggle', ghNode })

      // Force open the group if the user is actively searching, otherwise respect accordion state
      if (cleanSearch || openGhIds.has(ghNode.id)) {
        const addChildren = (nodes: TreeNode[]) => {
          nodes.forEach((node) => {
            if (!matchesSearch(node)) return

            if (node.reportCode === 'SH') {
              items.push({ type: 'sh-header', node })
              // Force open section headers if searching
              if (cleanSearch || !collapsedNodeIds.has(node.id))
                addChildren(node.children)
            } else if (node.reportCode === 'SSH') {
              items.push({ type: 'ssh-header', node })
              // Force open sub-section headers if searching
              if (cleanSearch || !collapsedNodeIds.has(node.id))
                addChildren(node.children)
            } else if (node.isEditable) {
              items.push({ type: 'leaf', node })
            }
          })
        }
        addChildren(ghNode.children)
      }
    })

    return items
  }, [budgetTree, openGhIds, collapsedNodeIds, searchTerm])

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: (i:any) => ITEM_HEIGHT[flatItems[i]?.type ?? 'leaf'],
    overscan: 8,
  })

  const allGhOpen = openGhIds.size === budgetTree.length
  const stickyLeft2 = isReadOnly ? 0 : COL_CHECKBOX

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
        {/* Redesigned Toolbar Container - Now a crisp White Card over your Gray background */}
        <div className="d-flex flex-column gap-1 pt-2 mb-2" id="ux_page_title">
          {/* Top Row: Title & Primary State Actions */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h1 className="h5 font-weight-700 mb-0 text-black">{t('title')}</h1>

            <div className="d-flex align-items-center gap-2">
              {/* Dynamic Batch Actions (Only shows when rows are selected) */}
              {selectedRows.length > 0 && !isReadOnly && (
                <div className="d-flex align-items-center gap-2 bg-light border rounded p-1 me-2">
                  <span
                    className="fw-bold text-secondary px-2"
                    style={{ fontSize: 13 }}
                  >
                    {selectedRows.length} {t('selected')}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleFillAcross}
                  >
                    {t('fill')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleDistributeEvenly}
                  >
                    {t('distribute')}
                  </button>
                </div>
              )}

              {/* Save / Update Budget Button - Brand Blue */}
              {fields.length > 0 && !isReadOnly && (
                <div className="filter-btn ms-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit((data) => {
                      saveBudget(data)
                      /* Reuse your processData logic */
                    })}
                  >
                    {isRecordsExists ? t('button.update') : t('button.save')}{' '}
                    {t('label.budget')}
                  </button>
                </div>
              )}

              {/* Publish Budget Button - Success Green */}
              {fields.length > 0 &&
                isRecordsExists &&
                !(isBudgetPublished || isPastYear) && (
                  <div className="filter-btn ms-auto ">
                    <button
                      type="button"
                      onClick={publishBudgetConfirmation}
                      className="button-tertiary"
                    >
                      <img src={publishBudgetIcon} alt="" />{' '}
                      {t('button.publishBudget')}
                    </button>
                  </div>
                )}
            </div>
          </div>

          {/* Bottom Row: Filters, Search, and File Actions */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 pt-1 border-top border-white">
            {/* Left Side: Search & Configuration Filters */}
            <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
              {/* Account Type Select */}
              {fields.length > 0 && (
                <div>
                  <CustomTreeSelect
                    budgetTree={originalRootsRef.current} // full unfiltered tree
                    value={selectedAccountType} // { id: number|null, label: string }
                    onChange={(selected: any) => {
                      setSelectedAccountType(selected)
                      onChangeAccountType(selected)
                    }}
                    placeholder={t('label.accountType')}
                  />
                </div>
              )}

              {/* Year Selection Group */}
              <div className="d-flex align-items-center gap-1">
                <div className="position-relative">
                  <Select
                    placeholder={
                      budgetSettings?.budget_based_on?.value == 1
                        ? t('label.selectCalendarYear')
                        : t('label.selectFiscalYear')
                    }
                    onChange={(e: any) => {
                      setSearchError('')
                      setSearchParams({ year: e })
                      fetchBudgetDetails(e.value)
                    }}
                    options={
                      budgetSettings?.budget_based_on?.value == 1
                        ? nextYears(5)
                        : nextInclomeTaxYears(10)
                    }
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    styles={{
                      container: (b) => ({ ...b, width: 220 }),
                      control: (b) => ({
                        ...b,
                        width: 220,
                        height: 38,
                        backgroundColor: '#f8f9fa',
                      }),
                      menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                    }}
                  />
                  {searchError && (
                    <span
                      className="text-danger position-absolute start-0 top-100"
                      style={{ fontSize: 11 }}
                    >
                      {searchError}
                    </span>
                  )}
                </div>

                {/* Generate Button - Subdued Indigo / Info styling */}
                <button
                  type="button"
                  className="btn btn-primary btn-sm text-white d-flex align-items-center gap-1"
                  style={{
                    height: '38px',
                    backgroundColor: '#0dcaf0',
                    borderColor: '#0dcaf0',
                  }}
                  onClick={() => fetchBudgetDetails(searchParams?.year?.value)}
                >
                  <img
                    src={filterSolidIcon}
                    alt=""
                    style={{
                      height: '14px',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                  {t('button.generate')}
                </button>
              </div>

              {/* Expand/Collapse Button - Subtle Minimal Style */}
              {fields.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                  style={{ height: '38px', fontSize: '13px' }}
                  onClick={() =>
                    setOpenGhIds(
                      allGhOpen
                        ? new Set()
                        : new Set(budgetTree.map((g) => g.id))
                    )
                  }
                >
                  <img
                    src={allGhOpen ? collapseIcon : expandIcon}
                    alt=""
                    style={{ height: '20px' }}
                  />
                  {allGhOpen ? t('label.collapseAll') : t('label.expandAll')}
                </button>
              )}
            </div>

            {/* Right Side: Consolidated File Actions Dropdown */}
            {fields.length > 0 && !isPastYear && (
              <div className="d-flex align-items-center gap-2">
                <div className="dropdown">
                  <button
                    className="btn btn-outline-dark dropdown-toggle d-flex align-items-center gap-2"
                    type="button"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ height: '38px', fontSize: '13px' }}
                  >
                    <i className="fa fa-folder-open me-1"></i>{' '}
                    {t('button.importExport', 'Import / Export')}
                  </button>
                  <ul
                    className="dropdown-menu dropdown-menu-end shadow"
                    aria-labelledby="dropdownMenuButton"
                  >
                    {!isBudgetPublished && (
                      <>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2"
                            type="button"
                            onClick={() => downloadTemplate()}
                          >
                            <i className="fa fa-download text-primary"></i>{' '}
                            {t('button.downloadTemplate', 'Download Template')}
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2"
                            type="button"
                            onClick={() => uploadBudget()}
                          >
                            <i className="fa fa-upload text-info"></i>{' '}
                            {t('button.uploadFile', 'Upload File')}
                          </button>
                        </li>
                      </>
                    )}

                    {/* Render Downloads inside the unified actions list when budget is published */}
                    {isBudgetPublished && (
                      <>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2"
                            type="button"
                            onClick={_handleDownloadExcel}
                          >
                            <i className="fa-solid fa-file-excel text-success"></i>{' '}
                            {t('button.downloadExcel')}
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2"
                            type="button"
                            onClick={_handleDownloadReport}
                          >
                            <i className="fa-solid fa-file-pdf text-danger"></i>{' '}
                            {t('button.downloadReport')}
                          </button>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Loader */}
        <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
          <div className="loader" />
        </div>

        {/* Empty state */}
        {fields.length === 0 && !isLoading && (
          <div className="executive-dashboard d-flex flex-column align-items-center justify-content-center p-1">
            <p className="text-muted mt-3">{t('table.notAvailable')}</p>
          </div>
        )}

        {/* Virtual table */}
        {fields.length > 0 && (
          <div
            ref={tableContainerRef}
            className="executive-dashboard v-table-outer"
          >
            <Form>
              <div className="v-table-inner">
                <BudgetTableHeader
                  tableHeaders={tableHeaders}
                  isReadOnly={isReadOnly}
                  monthTransform={monthTransform}
                  t={t}
                  allGhOpen={allGhOpen}
                  expandAll={() =>
                    setOpenGhIds(
                      allGhOpen
                        ? new Set()
                        : new Set(budgetTree.map((g) => g.id))
                    )
                  }
                />
                {flatItems.length > 0 ? (
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      position: 'relative',
                    }}
                  >
                    {virtualizer.getVirtualItems().map((virtualRow:any) => {
                      const item = flatItems[virtualRow.index]
                      if (!item) return null

                      const baseStyle: React.CSSProperties = {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }

                      // ── GH accordion toggle row (With Pinned Horizontal Sticky Inner Content) ──
                      if (item.type === 'gh-toggle') {
                        const isOpen = openGhIds.has(item.ghNode.id)
                        return (
                          <div
                            key={virtualRow.key}
                            className="v-table-row v-row-gh-toggle"
                            style={baseStyle}
                            onClick={() => toggleGh(item.ghNode.id)}
                          >
                            <div className="v-table-cell v-sticky-gh-content">
                              <i
                                className={
                                  isOpen
                                    ? 'fa fa-minus-circle icn-yellow'
                                    : 'fa fa-plus-circle icn-black'
                                }
                                aria-hidden="true"
                              />
                              <span
                                className="fw-semibold"
                                style={{
                                  color: '#000000',
                                  fontSize: 14,
                                  marginLeft: 5,
                                }}
                              >
                                {item.ghNode.account} —{' '}
                                {item.ghNode.accountName}
                              </span>
                            </div>
                          </div>
                        )
                      }

                      // ── SH header row ────────────────────────────
                      if (item.type === 'sh-header') {
                        const isCollapsed = collapsedNodeIds.has(item.node.id)
                        return (
                          <div
                            key={virtualRow.key}
                            className="v-table-row v-row-sh"
                            style={baseStyle}
                            onClick={() => toggleNode(item.node.id)}
                          >
                            {!isReadOnly && (
                              <div
                                className="v-table-cell v-sticky-left-1 justify-content-center"
                                style={{
                                  width: COL_CHECKBOX,
                                  minWidth: COL_CHECKBOX,
                                  background: '#eef0f8',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input wh-20 m-0"
                                  checked={collectFormIndexes(item.node).every(
                                    (i) => selectedRows.includes(i)
                                  )}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate =
                                        collectFormIndexes(item.node).some(
                                          (i) => selectedRows.includes(i)
                                        ) &&
                                        !collectFormIndexes(item.node).every(
                                          (i) => selectedRows.includes(i)
                                        )
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation()
                                    handleGroupSelectAll(
                                      collectFormIndexes(item.node)
                                    )
                                  }}
                                />
                              </div>
                            )}
                            <div
                              className="v-table-cell v-sticky-left-2"
                              style={{
                                width: COL_NAME,
                                minWidth: COL_NAME,
                                left: stickyLeft2,
                                background: '#eef0f8',
                              }}
                            >
                              <i
                                className={
                                  !isCollapsed
                                    ? 'fa fa-minus-circle icn-yellow m-2'
                                    : 'fa fa-plus-circle icn-black m-2'
                                }
                                aria-hidden="true"
                              />
                              {item.node.account} {item.node.accountName}
                            </div>
                          </div>
                        )
                      }

                      // ── SSH header row ───────────────────────────
                      if (item.type === 'ssh-header') {
                        const isCollapsed = collapsedNodeIds.has(item.node.id)
                        return (
                          <div
                            key={virtualRow.key}
                            className="v-table-row v-row-ssh"
                            style={baseStyle}
                            onClick={() => toggleNode(item.node.id)}
                          >
                            {!isReadOnly && (
                              <div
                                className="v-table-cell v-sticky-left-1 justify-content-center"
                                style={{
                                  width: COL_CHECKBOX,
                                  minWidth: COL_CHECKBOX,
                                  background: '#f5f6fb',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input wh-20 m-0"
                                  checked={collectFormIndexes(item.node).every(
                                    (i) => selectedRows.includes(i)
                                  )}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate =
                                        collectFormIndexes(item.node).some(
                                          (i) => selectedRows.includes(i)
                                        ) &&
                                        !collectFormIndexes(item.node).every(
                                          (i) => selectedRows.includes(i)
                                        )
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation()
                                    handleGroupSelectAll(
                                      collectFormIndexes(item.node)
                                    )
                                  }}
                                />
                              </div>
                            )}
                            <div
                              className="v-table-cell v-sticky-left-2"
                              style={{
                                width: COL_NAME,
                                minWidth: COL_NAME,
                                left: stickyLeft2 + 10,
                                background: '#f5f6fb',
                              }}
                            >
                              <i
                                className={
                                  !isCollapsed
                                    ? 'fa fa-minus-circle icn-yellow m-2'
                                    : 'fa fa-plus-circle icn-black m-2'
                                }
                                aria-hidden="true"
                              />
                              {item.node.account} {item.node.accountName}
                            </div>
                          </div>
                        )
                      }

                      // ── Editable leaf row ───────────────────────────
                      if (item.type === 'leaf') {
                        const { formIndex } = item.node
                        const data = fields[formIndex]
                        if (!data) return null
                        return (
                          <BudgetRow
                            key={virtualRow.key}
                            rowIndex={formIndex}
                            data={data}
                            control={control}
                            tableHeaders={tableHeaders}
                            isReadOnly={isReadOnly}
                            isSelected={selectedRows.includes(formIndex)}
                            onSelectRow={handleSelectRow}
                            onValueChange={onValueChange}
                            errors={errors}
                            bgColor="#ffffff"
                            style={baseStyle}
                          />
                        )
                      }

                      return null
                    })}
                  </div>
                ) : (
                  <div
                    className="v-table-cell v-sticky-left-2"
                    style={{
                      width: COL_NAME,
                      minWidth: COL_NAME,
                      left: stickyLeft2 + 10,
                      background: '#f5f6fb',
                    }}
                  >
                    <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                      <b>
                        {t('label.dataNotFound', 'No search results found')}
                      </b>
                    </Typography>
                  </div>
                )}
              </div>
            </Form>
            <ScrollToggle scrollRef={tableContainerRef} />
          </div>
        )}
      </div>

      {/* Hidden File Input for Budget Excel Upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx, .xls"
        onChange={handleFileSelect}
      />

      {/* Upload Validation Error Modal */}
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="h6 mb-0 text-white">
            <i
              className="fa fa-exclamation-triangle me-2"
              aria-hidden="true"
            ></i>
            {t('title.uploadErrors', 'Excel Validation Errors')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <p className="text-muted small mb-3">
            {t(
              'msg.uploadErrorsDesc',
              'Please fix the following errors in your Excel file and try uploading again:'
            )}
          </p>
          <table
            className="table table-sm table-striped table-bordered mb-0"
            style={{ fontSize: 13 }}
          >
            <thead className="table-dark">
              <tr>
                <th style={{ width: '90px' }}>{t('label.row', 'Row')}</th>
                <th style={{ width: '160px' }}>
                  {t('label.column', 'Column')}
                </th>
                <th>{t('label.errorMessage', 'Error Message')}</th>
              </tr>
            </thead>
            <tbody>
              {uploadErrors.map((err, idx) => (
                <tr key={idx}>
                  <td className="fw-bold text-danger">
                    {err.row > 0
                      ? `${t('label.row', 'Row')} ${err.row}`
                      : t('label.header', 'Header')}
                  </td>
                  <td className="fw-medium">{err.column || '-'}</td>
                  <td>{err.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setShowErrorModal(false)}
          >
            {t('button.close', 'Close')}
          </button>
        </Modal.Footer>
      </Modal>

      <SAlert
        msg={t('msg.publishConfirm')}
        show={showBudgetAlert['show']}
        title={t('title.confirm')}
        onConfirm={handlePublishBudget}
        confirmBtnText={t('button.yesPublish')}
        cancelBtnText={t('button.cancel')}
        showCancel={true}
        onCancel={closeBudgetModal}
      />
    </div>
  )
}

export default Budget
